import React from 'react'

import { LntSpin } from '@app/components/base'

import { isNull } from '@app/utils'
import { useExtendedNavigate, useExternalNavigate, useJwtSession, useNotification, useUserQuery } from '@app/hooks'

import { API_URL, SAML_PATH } from '@app/api'
import { tokenType } from '@app/constants/token'
import { errorDescription, errorMessage } from '@app/constants/errorNotification'

import permissionService from '@app/services/permissionService'
import authReturnUrlService, { canUseReturnUrl } from '@app/services/authReturnUrlService'

import { AuthCard } from './components'
import { useTokenFromQuery } from './hooks'

import styles from './styles.module.scss'

import type { UserDto } from '@app/api/routes/auth/dtos'
import type { TokenType } from '@app/services/jwtSessionService/types'

const innerUserAuthUrl = API_URL && API_URL + SAML_PATH

export const AuthPage: React.FC = () => {
  const navigate = useExtendedNavigate()
  const externalNavigate = useExternalNavigate()
  const notification = useNotification()
  const { session, setSession } = useJwtSession()

  const handleAuthorization = (): void => {
    innerUserAuthUrl && externalNavigate(innerUserAuthUrl)
  }

  const handleAuthSuccess =
    (type: TokenType) =>
      (token: string): void => {
        setSession({ type, token })
      }

  const handleGetUserSuccess = ({ roles }: UserDto): void => {
    const returnUrl = authReturnUrlService.getReturnUrl()
    const defaultPermittedRoute = permissionService.getDefaultPermittedRoute(roles)

    authReturnUrlService.clearReturnUrl()

    if (!isNull(returnUrl) && canUseReturnUrl(returnUrl, roles)) {
      navigate(returnUrl, { replace: true })

      return
    }

    if (isNull(defaultPermittedRoute)) {
      notification.error({
        message: errorMessage.ERROR,
        description: errorDescription.INSUFFICIENT_PERMISSIONS
      })

      return
    }

    navigate(defaultPermittedRoute)
  }

  useTokenFromQuery({ onAuthSuccess: handleAuthSuccess(tokenType.JWT) })

  const { isLoading: isUserLoading } = useUserQuery({
    enabled: !isNull(session),
    onSuccess: handleGetUserSuccess
  })

  return (
    <div className={ styles.authPage }>
      <LntSpin spinning={ !isNull(session) && isUserLoading }>
        <AuthCard onAuthorization={ handleAuthorization } />
      </LntSpin>
    </div>
  )
}
